import React from "react";
import { CircleAlert, ScanSearch } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import BodyPropertiesPanel from "./BodyPropertiesPanel";
import CameraPropertiesPanel from "./CameraPropertiesPanel";
import EqualityPropertiesPanel from "./EqualityPropertiesPanel";
import InertialPropertiesPanel from "./InertialPropertiesPanel";
import JointPropertiesPanel from "./JointPropertiesPanel";
import LightPropertiesPanel from "./LightPropertiesPanel";
import ObjectPropertiesPanel from "./ObjectPropertiesPanel";
import SitePropertiesPanel from "./SitePropertiesPanel";
import TendonPropertiesPanel from "./TendonPropertiesPanel";
import TendonWrapGeomPropertiesPanel from "./TendonWrapGeomPropertiesPanel";
import {
  InspectorMultiBodySelectionPanel,
  InspectorMultiMeshSelectionPanel,
} from "./InspectorMultiSelectionPanel";
import { useSelectionStore } from "../../stores/selectionStore";
import { useBodies, useCameras, useEqualities, useSceneActions } from "../../stores/sceneStore";
import { usePhysicsAuthoringLock, useReadOnlyProjectMode } from "../../utils/physicsGuards";
import { useInspectorMultiSelection } from "./useInspectorMultiSelection";
import PhysicsAuthoringLockNotice from "../ui/PhysicsAuthoringLockNotice";
import Separator from "../ui/Separator";
import BodyQuickAddSection from "./BodyQuickAddSection";

const LazyCollisionPropertiesPanel = React.lazy(() => import("./CollisionPropertiesPanel"));
const LazyVisualGeomPropertiesPanel = React.lazy(() => import("./VisualGeomPropertiesPanel"));

export const InspectorContentFallback = React.memo(function InspectorContentFallback() {
  return <div className="p-4 text-sm text-gray-300">Loading...</div>;
});

const MissingSelectionState = React.memo(function MissingSelectionState({ missingSelection }) {
  return (
    <div className="flex-1 flex items-center justify-center text-gray-400 py-8 px-4">
      <div className="text-center">
        <CircleAlert
          className="w-12 h-12 mx-auto mb-3 text-amber-300/70"
          strokeWidth={1.5}
          aria-hidden
        />
        <p className="text-sm font-medium text-gray-200">
          {missingSelection?.message || "Selected item no longer exists"}
        </p>
        <p className="mt-1 text-xs text-gray-400">
          Select another item in the scene to inspect it.
        </p>
      </div>
    </div>
  );
});

const quickNavProps = ({
  handleQuickNavBack,
  handleQuickNavForward,
  quickNavBackTarget,
  quickNavForwardTarget,
}) => ({
  quickNavBackTarget,
  onQuickNavBack: handleQuickNavBack,
  quickNavForwardTarget,
  onQuickNavForward: handleQuickNavForward,
});

const InspectorAuthoringLockNotice = React.memo(function InspectorAuthoringLockNotice() {
  const readOnlyProject = useReadOnlyProjectMode();
  return (
    <PhysicsAuthoringLockNotice
      actionText={
        readOnlyProject
          ? "Read-only project. Edit controls are hidden."
          : "Physics is running. Edit controls are hidden until simulation stops."
      }
      className="m-3 mb-0"
    />
  );
});

const withPhysicsLockNotice = (content) => (
  <>
    <InspectorAuthoringLockNotice />
    {content}
  </>
);

const EmptyInspectorState = React.memo(function EmptyInspectorState() {
  const bodies = useBodies();
  const cameras = useCameras();
  const equalities = useEqualities();
  const sceneActions = useSceneActions();
  const authoringLocked = usePhysicsAuthoringLock();

  return (
    <div className="p-3 space-y-4">
      <BodyQuickAddSection
        allowRootBody
        bodyUuid={null}
        bodies={bodies}
        cameras={cameras}
        equalities={equalities}
        hasInertia={false}
        disabled={authoringLocked}
        sceneActions={sceneActions}
        title="Create New"
      />

      <Separator />

      <div className="flex-1 flex items-center justify-center text-gray-400 py-8">
        <div className="text-center">
          <ScanSearch className="w-12 h-12 mx-auto mb-3 opacity-30" strokeWidth={1.5} aria-hidden />
          <p className="text-sm">Click an object in the scene to inspect it</p>
        </div>
      </div>
    </div>
  );
});

export default function InspectorPanelContent({
  activePanel,
  handleQuickNavBack,
  handleQuickNavForward,
  pendingVisualMeshParentBodyUuid,
  quickNavBackTarget,
  quickNavForwardTarget,
  setPendingVisualMeshParentBodyUuid,
  missingSelection,
}) {
  const { clearAllSelections, selectedBodies, setLastSelectionSource, setSelectedBodies } =
    useSelectionStore(
      useShallow((state) => ({
        clearAllSelections: state.clearAllSelections,
        selectedBodies: state.selectedBodies,
        setLastSelectionSource: state.setLastSelectionSource,
        setSelectedBodies: state.setSelectedBodies,
      }))
    );
  const {
    bodyVisibility,
    clearSelectedVisualMeshes,
    handleMultiBodyToggleVisibility,
    handleMultiMeshAssignMaterial,
    handleMultiMeshOriginDeltaChange,
    handleMultiMeshResetOrigins,
    handleMultiMeshSetCenterOrigin,
    handleMultiMeshSetStartOrigin,
    handleMultiMeshToggleVisibility,
    handleRemoveBodyFromMultiSelection,
    handleRemoveVisualMeshFromMultiSelection,
    isPhysicsRunning,
    meshVisibility,
    multiBodyChildUuids,
    multiBoundingBox,
    multiFocus,
    multiHighlight,
    multiMeshBoundingBox,
    multiMeshHighlight,
    multiMeshOriginMarker,
    multiMeshWireframe,
    multiMeshWireframeColor,
    selectedBodySummaryItems,
    selectedVisualMeshCount,
    selectedVisualMeshIds,
    selectedVisualMeshMaterialIds,
    selectedVisualMeshMaterialLabel,
    selectedVisualMeshSummaryItems,
    selectedVisualMeshTransformState,
    setMultiBoundingBox,
    setMultiFocus,
    setMultiHighlight,
    setMultiMeshBoundingBox,
    setMultiMeshHighlight,
    setMultiMeshOriginMarker,
    setMultiMeshWireframe,
    setMultiMeshWireframeColor,
    visualMeshMaterialOptions,
  } = useInspectorMultiSelection({
    clearAllSelections,
    selectedBodies,
    setLastSelectionSource,
    setSelectedBodies,
  });
  const navProps = quickNavProps({
    handleQuickNavBack,
    handleQuickNavForward,
    quickNavBackTarget,
    quickNavForwardTarget,
  });

  if (missingSelection) {
    return <MissingSelectionState missingSelection={missingSelection} />;
  }

  switch (activePanel) {
    case "camera":
      return withPhysicsLockNotice(<CameraPropertiesPanel {...navProps} />);
    case "equality":
      return withPhysicsLockNotice(<EqualityPropertiesPanel {...navProps} />);
    case "tendon":
      return withPhysicsLockNotice(<TendonPropertiesPanel {...navProps} />);
    case "joint":
      return withPhysicsLockNotice(<JointPropertiesPanel {...navProps} />);
    case "light":
      return withPhysicsLockNotice(<LightPropertiesPanel {...navProps} />);
    case "object":
      return withPhysicsLockNotice(<ObjectPropertiesPanel {...navProps} />);
    case "multiBody": {
      const childCount = multiBodyChildUuids.length;
      const allVisible = selectedBodies.every((uuid) => bodyVisibility[uuid] !== false);
      return (
        <InspectorMultiBodySelectionPanel
          allVisible={allVisible}
          childCount={childCount}
          isPhysicsRunning={isPhysicsRunning}
          multiBoundingBox={multiBoundingBox}
          multiFocus={multiFocus}
          multiHighlight={multiHighlight}
          onClearSelection={clearAllSelections}
          onRemoveBody={handleRemoveBodyFromMultiSelection}
          onToggleBoundingBox={() => setMultiBoundingBox((value) => !value)}
          onToggleFocus={() => setMultiFocus((value) => !value)}
          onToggleHighlight={() => setMultiHighlight((value) => !value)}
          onToggleVisibility={handleMultiBodyToggleVisibility}
          selectedBodies={selectedBodies}
          selectedBodySummaryItems={selectedBodySummaryItems}
        />
      );
    }
    case "multiMesh":
      return (
        <InspectorMultiMeshSelectionPanel
          clearSelectedVisualMeshes={clearSelectedVisualMeshes}
          handleMultiMeshAssignMaterial={handleMultiMeshAssignMaterial}
          handleMultiMeshOriginDeltaChange={handleMultiMeshOriginDeltaChange}
          handleMultiMeshResetOrigins={handleMultiMeshResetOrigins}
          handleMultiMeshSetCenterOrigin={handleMultiMeshSetCenterOrigin}
          handleMultiMeshSetStartOrigin={handleMultiMeshSetStartOrigin}
          handleMultiMeshToggleVisibility={handleMultiMeshToggleVisibility}
          isPhysicsRunning={isPhysicsRunning}
          meshVisibility={meshVisibility}
          multiMeshBoundingBox={multiMeshBoundingBox}
          multiMeshHighlight={multiMeshHighlight}
          multiMeshOriginMarker={multiMeshOriginMarker}
          multiMeshWireframe={multiMeshWireframe}
          multiMeshWireframeColor={multiMeshWireframeColor}
          selectedVisualMeshCount={selectedVisualMeshCount}
          selectedVisualMeshIds={selectedVisualMeshIds}
          selectedVisualMeshMaterialIds={selectedVisualMeshMaterialIds}
          selectedVisualMeshMaterialLabel={selectedVisualMeshMaterialLabel}
          selectedVisualMeshSummaryItems={selectedVisualMeshSummaryItems}
          selectedVisualMeshTransformState={selectedVisualMeshTransformState}
          setMultiMeshBoundingBox={setMultiMeshBoundingBox}
          setMultiMeshHighlight={setMultiMeshHighlight}
          setMultiMeshOriginMarker={setMultiMeshOriginMarker}
          setMultiMeshWireframe={setMultiMeshWireframe}
          setMultiMeshWireframeColor={setMultiMeshWireframeColor}
          visualMeshMaterialOptions={visualMeshMaterialOptions}
          onRemoveVisualMesh={handleRemoveVisualMeshFromMultiSelection}
        />
      );
    case "body":
      return withPhysicsLockNotice(<BodyPropertiesPanel {...navProps} />);
    case "geom":
      return withPhysicsLockNotice(<LazyCollisionPropertiesPanel {...navProps} />);
    case "visualGeom":
      return withPhysicsLockNotice(
        <LazyVisualGeomPropertiesPanel
          {...navProps}
          pendingParentBodyUuid={pendingVisualMeshParentBodyUuid}
          onPendingCancel={() => setPendingVisualMeshParentBodyUuid(null)}
          onPendingComplete={() => setPendingVisualMeshParentBodyUuid(null)}
        />
      );
    case "tendonWrapGeom":
      return withPhysicsLockNotice(<TendonWrapGeomPropertiesPanel />);
    case "site":
      return withPhysicsLockNotice(<SitePropertiesPanel {...navProps} />);
    case "inertial":
      return withPhysicsLockNotice(<InertialPropertiesPanel {...navProps} />);
    default:
      return <EmptyInspectorState />;
  }
}
